import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/seo-tools-hub';

const SEOToolsHubLanding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const title = 'SEO Tools Hub - Free Professional SEO Analysis | Explanatum';
    const desc = 'Free professional SEO tools analyzing your real Google and Bing data for maximum accuracy. Technical audits, Search Console analytics, and keyword research. Premium features coming soon.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What does the SEO Analyzer check?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The SEO Analyzer performs comprehensive website audits including page speed, mobile responsiveness, meta tags, heading structure, image optimization, internal linking, schema markup, and technical SEO issues. Get actionable recommendations to improve rankings.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I track rankings on both Google and Bing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! The SERP Tracker monitors your keyword rankings on both Google and Bing. Track unlimited keywords, monitor position changes, and identify ranking opportunities across both search engines.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does competitor analysis work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Enter competitor URLs to analyze their keyword strategy, backlink profile, content gaps, and ranking positions. Identify opportunities they are missing and keywords they are dominating.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does it cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The complete SEO Tools Hub is included with your Explanatum subscription at £15.20/month. No per-site fees, no keyword limits. Compare that to Ahrefs (£99/month), SEMrush (£100/month), or Moz (£79/month).',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDEwMCwyMDAsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-6">
              <span className="text-3xl">📊</span>
              <span className="text-sm font-semibold text-gray-700">Complete SEO Analysis & Monitoring</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Analyze Your Google & Bing Data,
              <span className="block bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Maximize SEO Accuracy
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Free professional SEO tools analyzing your real data from Google and Bing for maximum accuracy. Technical audits, Search Console insights, and keyword research—all free!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => {
                  track('cta_click', { location: 'hero', tool: 'seo_tools_hub' });
                  navigate('/marketing/seo-tools-hub');
                }}
                className="px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold rounded-xl hover:from-cyan-700 hover:to-blue-700 transition shadow-lg hover:shadow-xl text-lg"
              >
                Start Analyzing Now →
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('features');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition shadow-md text-lg"
              >
                Explore SEO Tools
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">FREE</div>
                <div className="text-sm text-gray-600">Professional SEO Tools</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-600">Real Google & Bing Data</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">3 Active</div>
                <div className="text-sm text-gray-600">Tools (3 more coming)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">6 Professional SEO Tools</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              <span className="text-green-600 font-semibold">3 active tools</span> analyzing your real Google & Bing data + <span className="text-purple-600 font-semibold">3 premium features</span> coming soon
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* SEO Analyzer */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">SEO Analyzer</h3>
              <p className="text-gray-600 mb-4">Comprehensive website audits covering speed, mobile responsiveness, meta tags, technical SEO, and actionable recommendations.</p>
            </div>

            {/* SERP Tracker */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">SERP Tracker</h3>
              <p className="text-gray-600 mb-4">Track keyword rankings on Google and Bing. Monitor position changes, identify trends, and discover ranking opportunities.</p>
            </div>

            {/* Keyword Research */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Keyword Research</h3>
              <p className="text-gray-600 mb-4">Discover high-value keywords with search volume, competition analysis, and keyword difficulty scores. Find opportunities competitors miss.</p>
            </div>

            {/* Competitor Analysis */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Competitor Analysis</h3>
              <p className="text-gray-600 mb-4">Analyze competitor strategies, keyword rankings, content gaps, and backlink profiles. Learn what works in your niche.</p>
            </div>

            {/* Backlink Monitor */}
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Backlink Monitor</h3>
              <p className="text-gray-600 mb-4">Track your backlink profile, monitor new links, identify toxic backlinks, and discover link building opportunities.</p>
            </div>

            {/* Web Traffic Analytics */}
            <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-8 border border-rose-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-rose-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Traffic Analytics</h3>
              <p className="text-gray-600 mb-4">Monitor organic traffic, track visitor behavior, analyze conversion paths, and measure SEO ROI with detailed analytics.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Switch to Explanatum?</h2>
            <p className="text-xl text-gray-600">Compare our value to expensive SEO tools</p>
          </div>

          <div className="max-w-4xl mx-auto overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg">
              <thead className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Feature</th>
                  <th className="px-6 py-4 text-center">Explanatum</th>
                  <th className="px-6 py-4 text-center">Ahrefs</th>
                  <th className="px-6 py-4 text-center">SEMrush</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 font-medium">Price/month</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">£15.20</td>
                  <td className="px-6 py-4 text-center text-gray-500">£99</td>
                  <td className="px-6 py-4 text-center text-gray-500">£100</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium">Site Audits</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Unlimited</td>
                  <td className="px-6 py-4 text-center text-gray-500">Limited</td>
                  <td className="px-6 py-4 text-center text-gray-500">Limited</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Keyword Tracking</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Unlimited</td>
                  <td className="px-6 py-4 text-center text-gray-500">500 keywords</td>
                  <td className="px-6 py-4 text-center text-gray-500">500 keywords</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-medium">Competitor Analysis</td>
                  <td className="px-6 py-4 text-center text-green-600">✓</td>
                  <td className="px-6 py-4 text-center text-gray-500">✓</td>
                  <td className="px-6 py-4 text-center text-gray-500">✓</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium">Content Tools</td>
                  <td className="px-6 py-4 text-center text-green-600">✓ Included</td>
                  <td className="px-6 py-4 text-center text-gray-500">Extra cost</td>
                  <td className="px-6 py-4 text-center text-gray-500">Extra cost</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <p className="text-2xl font-bold text-gray-900">Save £1,000+ per year vs competitors</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqSchema.mainEntity.map((faq, idx) => (
              <details key={idx} className="group bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {faq.name}
                  <span className="text-cyan-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Master SEO?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Get professional SEO tools for £15.20/month instead of £100+. No limits, no catches.
          </p>
          <button
            onClick={() => {
              track('cta_click', { location: 'footer', tool: 'seo_tools_hub' });
              navigate('/marketing/seo-tools-hub');
            }}
            className="px-10 py-4 bg-white text-cyan-600 font-bold rounded-xl hover:bg-gray-100 transition shadow-xl text-lg"
          >
            Start Your SEO Analysis →
          </button>
        </div>
      </section>
    </div>
  );
};

export default SEOToolsHubLanding;
